import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type {
	BuildJob,
	BuildProfile,
	CreateBuildProfileBody,
	EnqueueBuildBody,
	GenerateBuilderUploadUrlBody,
	GenerateBuilderUploadUrlResponse,
	ListBuildJobsParams,
	ListBuildJobsResponse,
	ListBuildProfilesParams,
	ListBuildProfilesResponse,
	UpdateBuildProfileBody,
} from './types/builder'

export const builderApi = createApi({
	reducerPath: 'builderApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['BuildProfile', 'BuildJob'],
	endpoints: (builder) => ({
		listBuildProfiles: builder.query<
			ListBuildProfilesResponse,
			ListBuildProfilesParams | void
		>({
			query: (params) => ({ url: '/builder/profiles', params: params ?? {} }),
			providesTags: (result) =>
				result
					? [
							...result.profiles.map((p) => ({ type: 'BuildProfile' as const, id: p.id })),
							{ type: 'BuildProfile' as const, id: 'LIST' },
						]
					: [{ type: 'BuildProfile' as const, id: 'LIST' }],
		}),

		getBuildProfile: builder.query<BuildProfile | null, string>({
			query: (id) => `/builder/profiles/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'BuildProfile', id }],
		}),

		createBuildProfile: builder.mutation<BuildProfile | null, CreateBuildProfileBody>({
			query: (body) => ({ url: '/builder/profiles', method: 'POST', body }),
			invalidatesTags: [{ type: 'BuildProfile', id: 'LIST' }],
		}),

		updateBuildProfile: builder.mutation<
			BuildProfile | null,
			{ id: string; body: UpdateBuildProfileBody }
		>({
			query: ({ id, body }) => ({ url: `/builder/profiles/${id}`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'BuildProfile', id },
				{ type: 'BuildProfile', id: 'LIST' },
			],
		}),

		/** Soft delete — the backend sets `isActive: false`. */
		deleteBuildProfile: builder.mutation<BuildProfile | null, string>({
			query: (id) => ({ url: `/builder/profiles/${id}`, method: 'DELETE' }),
			invalidatesTags: (_r, _e, id) => [
				{ type: 'BuildProfile', id },
				{ type: 'BuildProfile', id: 'LIST' },
			],
		}),

		/** Presigned S3 PUT URL for the app logo — upload, then use `publicUrl` in `branding.logo`. */
		generateBuilderUploadUrl: builder.mutation<
			GenerateBuilderUploadUrlResponse,
			GenerateBuilderUploadUrlBody
		>({
			query: (body) => ({ url: '/builder/upload-url', method: 'POST', body }),
		}),

		enqueueBuild: builder.mutation<
			BuildJob,
			{ profileId: string } & EnqueueBuildBody
		>({
			query: ({ profileId, ...body }) => ({
				url: `/builder/profiles/${profileId}/builds`,
				method: 'POST',
				body,
			}),
			invalidatesTags: (_r, _e, { profileId }) => [
				{ type: 'BuildJob', id: `LIST-${profileId}` },
			],
		}),

		listBuildJobs: builder.query<ListBuildJobsResponse, ListBuildJobsParams>({
			query: ({ profileId, ...params }) => ({
				url: `/builder/profiles/${profileId}/jobs`,
				params,
			}),
			providesTags: (result, _e, { profileId }) =>
				result
					? [
							...result.jobs.map((j) => ({ type: 'BuildJob' as const, id: j.id })),
							{ type: 'BuildJob' as const, id: `LIST-${profileId}` },
						]
					: [{ type: 'BuildJob' as const, id: `LIST-${profileId}` }],
		}),

		getBuildJob: builder.query<BuildJob | null, string>({
			query: (id) => `/builder/jobs/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'BuildJob', id }],
		}),
	}),
})

export const {
	useListBuildProfilesQuery,
	useGetBuildProfileQuery,
	useCreateBuildProfileMutation,
	useUpdateBuildProfileMutation,
	useDeleteBuildProfileMutation,
	useGenerateBuilderUploadUrlMutation,
	useEnqueueBuildMutation,
	useListBuildJobsQuery,
	useGetBuildJobQuery,
} = builderApi
